import { globalLoggerToken, growaveConfigurationToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import { container, singleton } from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import CurrencyProviderInterface from "./CurrencyProvider/CurrencyProviderInterface";
import StaticCurrencyProvider from "./CurrencyProvider/StaticCurrencyProvider";
import MoneyFormatter from "./Formatters/MoneyFormatter/MoneyFormatter";
import MoneyFormatterInterface from "./Formatters/MoneyFormatter/MoneyFormatterInterface";
import GwCurrency from "./GwCurrency/GwCurrency";
import HTMLSanitizerInterface from "./HTMLSanitizer/HTMLSanitizerInterface";
import XSSHTMLSanitizer from "./HTMLSanitizer/XSSHTMLSanitizer";
import FakeMoneyExchanger from "./MoneyExchanger/FakeMoneyExchanger";
import LegacyMoneyExchanger from "./MoneyExchanger/LegacyMoneyExchanger";
import MoneyExchangerInterface from "./MoneyExchanger/MoneyExchangerInterface";

interface MoneyModuleExports {
    moneyExchanger: MoneyExchangerInterface;
    moneyFormatter: MoneyFormatterInterface;
    currencyProvider: CurrencyProviderInterface;
    htmlSanitizer: HTMLSanitizerInterface;
    gwCurrency: GwCurrency;
}

@singleton()
export class MoneyModule implements ModuleInterface<MoneyModuleExports> {
    exports: MoneyModuleExports;

    public constructor() {
        const moneyModuleLogger = container
            .resolve(globalLoggerToken)
            .newLogger("MoneyModule");

        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const currencyProvider = new StaticCurrencyProvider(
            window.Shopify?.currency?.active || "USD",
            moneyModuleLogger
        );

        const gwCurrency = new GwCurrency(moneyModuleLogger, new ApiClient());

        if (!growaveConfiguration.initedFromSdk) {
            gwCurrency.loadRates();
        }

        let moneyExchanger: MoneyExchangerInterface;

        if (
            growaveConfiguration.storefrontApiAccessToken ||
            //  use fake money exchanger if the module is initialized from the SDK,
            //  because LegacyMoneyExchanger will make attempt to load currencies.js
            growaveConfiguration.initedFromSdk
        ) {
            moneyExchanger = new FakeMoneyExchanger();
        } else {
            moneyExchanger = new LegacyMoneyExchanger(
                growaveConfiguration.shopCurrency,
                gwCurrency
            );
        }

        const moneyFormatter = new MoneyFormatter(
            growaveConfiguration.moneyFormat,
            growaveConfiguration.moneyWithCurrencyFormat
        );

        const htmlSanitizer = new XSSHTMLSanitizer(
            moneyModuleLogger,
            {
                span: ["class", "id", "style"],
                div: ["class", "id"],
                p: ["class", "id"],
                i: ["class", "id"],
                b: ["class", "id"],
                br: [],
                pre: ["class", "id"],
                small: ["class", "id"],
                strong: ["class", "id"],
                em: ["class", "id"],
                ul: ["class", "id"],
                ol: ["class", "id"],
                li: ["class"],
                a: ["href", "target", "class", "id"],
                svg: [
                    "class",
                    "id",
                    "width",
                    "height",
                    "viewBox",
                    "style",
                    "xmlns",
                ],
                path: ["fill", "d"],
                img: ["src", "alt", "class", "id", "style"],
            },
            {
                display: true,
                "align-items": true,
                gap: true,
                width: true,
                height: true,
                "object-fit": true,
            }
        );

        this.exports = {
            moneyExchanger,
            moneyFormatter,
            currencyProvider,
            htmlSanitizer,
            gwCurrency,
        };
    }
    public registerProviders() {
        return {};
    }
}
